import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, fromEvent, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { Role } from '../models/role.enum';
import { LmsActivityHeartbeatService } from '../services/lms-activity-heartbeat.service';

/** 5 minutes of inactivity before session expires. */
export const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Tracks student activity and expires the session after idle timeout.
 * Admin / content-creator / instructor areas are not watched.
 * On expiry, flushes LMS activity heartbeat then clears the local session.
 */
@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private router = inject(Router);
  private auth = inject(AuthService);
  private zone = inject(NgZone);
  private heartbeat = inject(LmsActivityHeartbeatService);

  private timerId: ReturnType<typeof setTimeout> | null = null;
  private activitySub?: Subscription;
  private routerSub?: Subscription;
  private watching = false;

  /** When the learner entered a protected area (session start). */
  private sessionStartedAtMs: number | null = null;

  /** True when the blocking session-expired modal must be shown. */
  readonly expired = signal(false);

  /** Last protected area — used to pick the right login page. */
  private lastAppUrl = '/etudiant';

  /** Last reported duration (ms) for UI / debugging. */
  readonly lastSessionDurationMs = signal<number | null>(null);

  init(): void {
    if (this.routerSub) {
      return;
    }

    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects || event.url;
        if (this.isAuthRoute(url) || !this.shouldWatch(url)) {
          this.stopWatching();
          return;
        }
        this.lastAppUrl = url;
        if (!this.expired()) {
          this.startWatching();
        }
      });

    const current = this.router.url;
    if (this.shouldWatch(current) && !this.expired()) {
      this.lastAppUrl = current;
      this.startWatching();
    }
  }

  ngOnDestroy(): void {
    this.stopWatching();
    this.routerSub?.unsubscribe();
  }

  /** Clears expired state after user acknowledges and leaves for login. */
  acknowledge(): void {
    this.expired.set(false);
    this.lastSessionDurationMs.set(null);
    this.sessionStartedAtMs = null;
  }

  loginRedirectUrl(): string {
    return '/connexion';
  }

  private startWatching(): void {
    if (this.watching || this.expired()) {
      return;
    }
    this.watching = true;
    if (this.sessionStartedAtMs == null) {
      this.sessionStartedAtMs = Date.now();
    }

    this.zone.runOutsideAngular(() => {
      this.activitySub = merge(
        fromEvent(document, 'mousedown', { capture: true }),
        fromEvent(document, 'click', { capture: true }),
        fromEvent(document, 'keydown', { capture: true }),
        fromEvent(document, 'touchstart', { capture: true }),
        fromEvent(document, 'wheel', { capture: true }),
        fromEvent(document, 'input', { capture: true })
      ).subscribe(() => this.resetTimer());
    });

    this.resetTimer();
  }

  private stopWatching(): void {
    this.watching = false;
    this.clearTimer();
    this.activitySub?.unsubscribe();
    this.activitySub = undefined;
  }

  private resetTimer(): void {
    if (!this.watching || this.expired()) {
      return;
    }
    this.clearTimer();
    this.timerId = setTimeout(() => {
      this.zone.run(() => this.expireSession());
    }, INACTIVITY_TIMEOUT_MS);
  }

  private clearTimer(): void {
    if (this.timerId != null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private expireSession(): void {
    if (this.expired()) {
      return;
    }
    this.stopWatching();

    if (this.sessionStartedAtMs != null) {
      this.lastSessionDurationMs.set(Math.max(0, Date.now() - this.sessionStartedAtMs));
    }

    // Flush LMS activity while auth token is still available, then clear session.
    this.heartbeat.stop();
    this.auth.invalidateSession();
    this.expired.set(true);
  }

  private isAuthRoute(url: string): boolean {
    return url.startsWith('/connexion') || url.startsWith('/unauthorized');
  }

  /** Inactivity expiry applies only to learner (student) sessions. */
  private shouldWatch(url: string): boolean {
    if (!url.startsWith('/etudiant')) {
      return false;
    }
    const role = this.auth.currentRole();
    return !role || role === Role.Student;
  }
}
